import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Message } from '@bwoty-web/ui-kit';
import { useEffect } from 'react';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import { bindActionCreators } from 'redux';

import FadeIn from '../utils/fadeIn';
import GalleryLoader from '../components/shared/loaders/GalleryLoader';
import LoaderHeading from '../components/shared/loaders/LoaderHeading';
import CruiseListHits from '../components/cruiseList/CruiseListHits';
import CruiseCardList from '../components/cruiseList/CruiseCardList';
import CruiseSortPanel from '../components/cruiseList/CruiseSortPanel';
import MessageContainer from '../components/shared/message/MessageContainer';
import CruiseFilterPanel from '../components/cruiseList/filter/CruiseFilterPanel';
import { urlUtils } from '../utils/url';
import { texts, type } from '../assets/texts';
import { sortedCruiseListSelector } from '../redux/selectors/cruiseList/sortSelectors';
import { filteredCruisePackagesSelector } from '../redux/selectors/cruiseList/filterSelectors';
import {
  globeLoaderImages,
  loaderImages,
} from '../assets/imageUrls/cruiseLoaderImages';

import * as CruiseListActions from '../redux/actions/cruiseListActions';

import './cruiseListContainer.scss';

const CruiseListRootContainer = ({
  actions,
  filteredAndSortedCruises,
  initialized,
  labels,
  message,
  originalCruiseList,
  siteSettings,
  userId,
}) => {
  const isGlobetrotter = useIsGlobetrotter();
  useEffect(() => {
    actions.init();
  }, [actions]);

  const hasSessionId = !!urlUtils.getSessionIdFromUrl();

  if (!initialized) {
    if (hasSessionId) {
      return (
        <LoaderHeading
          heading={texts[siteSettings.siteId].loaderHeadingShort}
          topMargin
        />
      );
    }

    return (
      <GalleryLoader
        siteSettings={siteSettings}
        imageList={isGlobetrotter ? globeLoaderImages : loaderImages}
        heading={texts[siteSettings.siteId].loaderHeading}
        tips={texts[siteSettings.siteId].tips.filter(
          (tip) => tip.type === type.BOTH || tip.type === type.CRUISE,
        )}
      />
    );
  }

  const isZeroHit = originalCruiseList?.length <= 0;
  const filteredToZeroHits =
    !isZeroHit > 0 && filteredAndSortedCruises?.length === 0;

  return (
    <FadeIn>
      <div className="iw-cruise-list-container">
        <MessageContainer
          message={message}
          user={!isZeroHit ? userId : ''}
          labels={labels}
          style={{ marginBottom: '24px' }}
        />
        {!isZeroHit && (
          <>
            <div className="iw-cruise-list__filter-sort-container">
              <CruiseFilterPanel />
              <CruiseSortPanel />
            </div>
            <CruiseListHits />
            <div className="iw-cruise-list">
              {filteredToZeroHits && (
                <Message
                  header={labels.zeroHitTooltipText}
                  messageType="info"
                />
              )}
              <CruiseCardList />
            </div>
          </>
        )}
      </div>
    </FadeIn>
  );
};

CruiseListRootContainer.propTypes = {
  actions: PropTypes.shape({
    init: PropTypes.func.isRequired,
    toggleFilterPanel: PropTypes.func.isRequired,
    toggleSortPanel: PropTypes.func.isRequired,
  }).isRequired,
  filteredAndSortedCruises: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialized: PropTypes.bool.isRequired,
  labels: PropTypes.shape({
    zeroHitTooltipText: PropTypes.string.isRequired,
  }),
  message: PropTypes.shape({
    message: PropTypes.string,
    severity: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  originalCruiseList: PropTypes.arrayOf().isRequired,
  siteSettings: PropTypes.shape({
    imageDomain: PropTypes.string.isRequired,
    siteId: PropTypes.number.isRequired,
  }).isRequired,
  userId: PropTypes.string.isRequired,
};

CruiseListRootContainer.defaultProps = {
  labels: {},
  message: null,
};

const mapActions = (dispatch) => {
  return {
    actions: bindActionCreators({ ...CruiseListActions }, dispatch),
  };
};

const mapStateToProps = (state) => {
  const newState = state.toJS();
  const { isInspiration } = newState.common.siteSettings;
  const originalCruiseList = newState.cruises.cruises;

  return {
    filteredAndSortedCruises: isInspiration
      ? filteredCruisePackagesSelector(newState)
      : sortedCruiseListSelector(newState),
    initialized: newState.cruises.initialized,
    labels: newState.labels,
    message: newState.message,
    originalCruiseList,
    siteSettings: newState.common.siteSettings,
    userId: newState.common.userId,
  };
};

export default connect(mapStateToProps, mapActions)(CruiseListRootContainer);
